import { useQuery, UseQueryOptions } from '@tanstack/react-query'
import { APIList } from 'src/interfaces'
import { Entities } from 'src/enums'
import { ProductType } from 'src/api'
import { useApiInstance } from './useApiInstance'
import {
  buildRequestParams,
  ObjectType,
  transformPage,
  transformPerPage,
} from './helpers'

interface RequestParams {
  params?: Partial<AdminProductTypeGetParams>;
  paramsTransform?: Array<(params: Record<string, any>) => Record<string, any>>;
  options?: Omit<
    UseQueryOptions<unknown, Error, APIList<ProductType>>,
    'queryKey' | 'queryFn'
  >;
}

interface AdminProductTypeGetParams {
  limit: number;
  offset: number;
  sort: string;
  withTips: string;
}

const schema: ObjectType['schema'] = {
  limit: 'number',
  offset: 'number',
  sort: 'string',
  withTips: 'string',
}

export const useProductTypeListRequest = ({
  params = {},
  paramsTransform = [],
  options,
}: RequestParams = {}) => {
  const api = useApiInstance()

  const transformedParams = [
    transformPerPage,
    transformPage,
    ...paramsTransform,
  ].reduce((param, transform) => transform(param), params)

  const queryParams = buildRequestParams<AdminProductTypeGetParams>(
    transformedParams,
    schema,
  )

  return useQuery<unknown, Error, APIList<ProductType>>(
    [Entities.ProductTypes, JSON.stringify(transformedParams)],
    async () => {
      const {
        limit,
        offset,
        sort,
        withTips,
      } = queryParams
      const { data } = await api.v1AdminProductsTypesGet(
        limit,
        offset,
        sort,
        withTips,
      )
      return data
    },
    {
      enabled: Object.keys(transformedParams).length > 0,
      ...options,
    },
  )
}
